import React, {useState} from 'react'
import {AUDIENCE} from '../../../../../utils/constants'
import {
  FormControlLabel,
  Stack,
  Grid,
  IconButton,
  Radio,
  Typography,
  TextField,
} from '@mui/material'
import WbTextField from '../../../../common/WbTextField'
import RadioGroup from '@mui/material/RadioGroup'
import questionResponseTemplate from '../../../../../utils/questionResponseTemplate'
import {Config} from '../../../../../utils/Config'
import WbRadioButton, {
  CustomRadioButton,
} from '../../../../common/WbRadioButton'
import SurveyEmoji from '../../../../common/SurveyEmoji'
import {DummyImage} from '../../../../common/DummyImage'
import QuestionCategory from '../../../../../utils/questionCategory'
import QuestionCategoryBadge from '../QuestionCategoryBadge'
import {optionEmoji} from './likertScaleSource'

export default function MultipleOptionRender({
  question,
  audience,
  updateAnswers,
  language = 'en',
}) {
  const [value, setValue] = useState(null)
  const [response, setResponse] = useState({
    ...questionResponseTemplate,
    questionId: question.id,
    hasNumValue: question.hasCustomValue,
    participantTypeText:
      audience === AUDIENCE.STAFF
        ? 'Staff'
        : audience === AUDIENCE.PARENT
          ? 'Parent'
          : 'Pupil',
    questionTypeText: 'Multiple Option',
  })

  const setSelectedOption = option => {
    
    setValue(option)
    let resp = {
      ...response,
      followupComment:
        question.hasFollowup &&
        question?.followupQuestions?.findIndex(
          x => x.followupValue == value?.value
        ) > -1
          ? response.followupComment
          : null,
    }
    if (question.categoryId) {
      let category = QuestionCategory.getQuestionCategoryFromId(
        question.categoryId
      )
      resp.categoryId = question.categoryId
      resp.categoryName = category ? category.name : null
    }
    if (language === 'en') {
      resp[`valueText1`] =
        audience === AUDIENCE.STAFF
          ? option.staffText
          : AUDIENCE.PARENT == audience
            ? option.parentText
            : option.pupilText
      resp['staffQuestion'] = question.staffText
      resp['parentQuestion'] = question.parentText
      resp['studentQuestion'] = question.pupilText
    } else {
      resp[`valueText1`] =
        audience === AUDIENCE.STAFF
          ? option.staffTextEnglish
          : AUDIENCE.PARENT == audience
            ? option.parentTextEnglish
            : option.pupilTextEnglish
      resp['staffQuestion'] = question.staffTextEnglish
      resp['parentQuestion'] = question.parentTextEnglish
      resp['studentQuestion'] = question.pupilTextEnglish
    }

    resp[`numValue1`] =
      question.hasCustomValue && option.numValue ? option.numValue : null

    question?.multiOptionResponseAttributes.map((op, i) => {
      if (op.value === option.value) {
        resp[`value${i + 1}`] = option.value
      } else {
        resp[`value${i + 1}`] = null
      }
    })
    setResponse(resp)
    if (updateAnswers) {
      updateAnswers(resp)
    }
  }

  const renderTextTypeOptions = () => {
    return (
      <Stack direction="column" spacing={1}>
        <div className="radio-button-container">
          <RadioGroup value={value?.value}>
            {question?.multiOptionResponseAttributes
              ?.sort((x, y) => x.order - y.order)
              .map((option, index) => (
                <FormControlLabel
                  control={
                    <Radio
                      checked={response[`value${index + 1}`] !== null}
                      onChange={e => setSelectedOption(option)}
                    />
                  }
                  label={
                    AUDIENCE.STAFF === audience
                      ? option.staffText
                      : AUDIENCE.PARENT == audience
                        ? option.parentText
                        : option.pupilText
                  }
                />
              ))}
          </RadioGroup>
        </div>
      </Stack>
    )
  }

  const renderEmojiTypeOptions = () => {
    return (
      <Stack
        direction="row"
        spacing={{xs: 5, sm: 5, md: 7}}
        useFlexGap
        justifyContent={'center'}
        flexWrap={'wrap'}
        rowGap={{xs: 3, sm: 5, md: 7}}
        mt={2}
      >
        {question?.multiOptionResponseAttributes
          ?.sort((x, y) => x.order - y.order)
          .map((option, index) => (
            <Stack direction="column" spacing={1}>
              <div
                style={{
                  border:
                    value?.value === option.value
                      ? '2px solid #007A7A'
                      : '2px solid transparent',
                }}
              >
                {option.staffEmoji ||
                option.parentEmoji ||
                option.pupilEmoji ? (
                  <IconButton
                    onClick={e => setSelectedOption(option)}
                    disableRipple
                  >
                    <SurveyEmoji
                      // Resolve the emoji with the same cross-audience fallback the editor uses
                      // (likertScaleSource.optionEmoji). Bank/template questions often store the
                      // emoji only on staffEmoji; a parent/student survey leaves parentEmoji/pupilEmoji
                      // empty, so reading the raw audience field rendered a blank emoji in the preview
                      // even though the editor (which falls back) showed it.
                      unified={optionEmoji(option, audience)}
                      size={66}
                    />
                  </IconButton>
                ) : (
                  <DummyImage width={98} height={72} />
                )}
              </div>
              <div style={{marginTop: 10}}>
                <Typography variant="body1">
                  {audience === AUDIENCE.STAFF && option.showStaffEmojiLabel
                    ? option.staffText
                    : ''}
                  {audience === AUDIENCE.PARENT && option.showParentEmojiLabel
                    ? option.parentText
                    : ''}
                  {audience === AUDIENCE.STUDENT && option.showPupilEmojiLabel
                    ? option.pupilText
                    : ''}
                </Typography>
              </div>
            </Stack>
          ))}
      </Stack>
    )
  }

  const renderImageTypeOptions = () => {
    return (
      <Stack
        direction="row"
        columnGap={{xs: 5, sm: 5, md: 7}}
        useFlexGap
        justifyContent={'center'}
        flexWrap={'wrap'}
        rowGap={{xs: 3, sm: 5, md: 7}}
        mt={2}
      >
        {question?.multiOptionResponseAttributes
          ?.sort((x, y) => x.order - y.order)
          .map((option, index) => (
            <Stack direction="column" spacing={1}>
              <div
                style={{
                  border:
                    value?.value === option.value
                      ? '2px solid #007A7A'
                      : '2px solid transparent',
                }}
              >
                <IconButton
                  onClick={e => setSelectedOption(option)}
                  disableRipple
                  sx={{padding: 0}}
                >
                  {option.staffImage ||
                  option.parentImage ||
                  option.pupilImage ? (
                    <img
                      src={`${Config.BASE_URL}/Artifact/download?fileName=${
                        audience === AUDIENCE.STAFF
                          ? option.staffImage
                          : audience === AUDIENCE.PARENT
                            ? option.parentImage
                            : option.pupilImage
                      }`}
                      style={{width: 200, height: 'auto'}}
                    />
                  ) : (
                    <DummyImage width={98} height={72} />
                  )}
                </IconButton>
              </div>
              <div style={{marginTop: 10}}>
                <Typography variant="body1">
                  {option.showStaffImageLabel && AUDIENCE.STAFF === audience
                    ? option.staffText
                    : ''}
                  {option.showParentImageLabel && AUDIENCE.PARENT === audience
                    ? option.parentText
                    : ''}
                  {option.showPupilImageLabel && AUDIENCE.STUDENT === audience
                    ? option.pupilText
                    : ''}
                </Typography>
              </div>
            </Stack>
          ))}
      </Stack>
    )
  }

  const renderFollowupQuestion = () => {
    return (
      <Stack direction="column" spacing={2} mt={1}>
        <Stack>
          <Typography variant="h2">
            {AUDIENCE.STAFF === audience
              ? question.followupQuestions[
                  question.followupQuestions.findIndex(
                    x => x.followupValue === value?.value
                  )
                ]?.staffFollowupText
              : AUDIENCE.PARENT === audience
                ? question.followupQuestions[
                    question.followupQuestions.findIndex(
                      x => x.followupValue === value?.value
                    )
                  ]?.parentFollowupText
                : question.followupQuestions[
                    question.followupQuestions.findIndex(
                      x => x.followupValue === value?.value
                    )
                  ]?.pupilFollowupText}
          </Typography>
        </Stack>

        <Stack>
          <TextField
            multiline
            rows={4}
            label={'Enter your comment'}
            value={response.followupComment}
            onChange={e => {
              setResponse({
                ...response,
                followupComment: e.target.value,
              })

              if (updateAnswers) {
                updateAnswers({
                  ...response,
                  followupComment: e.target.value,
                })
              }
            }}
          ></TextField>
        </Stack>
      </Stack>
    )
  }
  return (
    <Grid container>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <Stack direction="column" alignItems="center" spacing={1.5}>
          {/* <QuestionCategoryBadge categoryName={question.categoryName} /> */}
          <Typography variant="h1">
            {AUDIENCE.STAFF === audience
              ? question.staffText
              : AUDIENCE.PARENT == audience
                ? question.parentText
                : question.pupilText}
          </Typography>
        </Stack>
      </Grid>

      {question.image && (
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <img
            src={`${Config.BASE_URL}/Artifact/download?fileName=${question.image}`}
            alt="question image"
            style={{width: 150, height: 'auto'}}
          />
        </Grid>
      )}
      {question.hasDescription && (
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Typography variant="body1" mb={2} className='ques-desc'>
            {AUDIENCE.STAFF === audience
              ? question.staffDescription
              : AUDIENCE.PARENT == audience
                ? question.parentDescription
                : question.pupilDescription}
          </Typography>
        </Grid>
      )}
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <div className="question-type-answer">
          {question.isTextOption
            ? renderTextTypeOptions()
            : question.isEmojiOption
              ? renderEmojiTypeOptions()
              : renderImageTypeOptions()}
          {question.hasFollowup &&
            question.followupQuestions?.findIndex(
              x => x.followupValue === value?.value
            ) > -1 &&
            renderFollowupQuestion()}
        </div>
      </Grid>
    </Grid>
  )
}
